import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { formatDate } from '../../../utils/formatters'
import { usePreferences } from '../../../hooks/usePreferences'
import { calculateEOLDate } from '../../../utils/assetUtils'

export default function EOLBudgetReport({ assets = [] }) {
  const { t } = useTranslation()

  const eolData = useMemo(() => {
    if (!assets || assets.length === 0) return { eolAssets: [], totalBudgetNeeded: 0 }

    const today = new Date()
    today.setHours(0, 0, 0, 0)

    const eolAssets = assets.filter((asset) => {
      if (asset?.is_archived) return false

      const status = (asset?.status || '').toLowerCase()
      const deadStatuses = ['broken', 'stolen', 'retired', 'written-off', 'written off', 'lost']
      if (deadStatuses.includes(status)) return false

      const acquiredRaw = asset.purchase_date || asset.created_at || null
      if (!acquiredRaw) return false

      // Calculate asset-specific EOL date based on its registered lifespan (defaulting to 5 years)
      const eolDate = calculateEOLDate(acquiredRaw, asset.lifespan_years || 5)
      if (!eolDate) return false

      return eolDate < today
    })

    // Sum actual purchase prices of the EOL assets (defaulting to 3500 if missing or 0)
    const totalBudgetNeeded = eolAssets.reduce((sum, asset) => sum + (parseFloat(asset.purchase_price) || 3500), 0)

    return { eolAssets, totalBudgetNeeded }
  }, [assets])

  const { eolAssets, totalBudgetNeeded } = eolData
  const replacementCount = eolAssets.length
  const estimateText = `${replacementCount} ${t('lifecycle.asset')}${replacementCount === 1 ? '' : 's'} past expected lifespan.`
  const { preferences } = usePreferences()
  const preferredFormat = preferences?.date_format || 'DD/MM/YYYY'

  return (
    <div className="bg-card shadow rounded-lg p-4">
      <h3 className="text-lg font-semibold mb-2">{t('lifecycle.eol_budget_forecast')}</h3>

      <div className="flex items-baseline justify-between">
        <div>
          <div className="text-2xl font-bold">RM {totalBudgetNeeded.toLocaleString()}</div>
          <div className="text-sm text-muted-foreground">{estimateText}</div>
        </div>
      </div>

      <div className="mt-4 max-h-40 overflow-auto">
        {eolAssets.length === 0 ? (
          <div className="text-sm text-green-600">{t('lifecycle.eol_all_clear')}</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-left text-xs text-muted-foreground border-b">
              <tr>
                <th className="py-1">{t('lifecycle.asset_tag')}</th>
                <th className="py-1">{t('lifecycle.name')}</th>
                <th className="py-1">{t('lifecycle.purchase_date')}</th>
              </tr>
            </thead>
            <tbody>
              {eolAssets.map((a) => {
                const dateRaw = a.purchase_date || a.created_at || ''
                const dateStr = dateRaw ? formatDate(dateRaw, preferredFormat) : t('lifecycle.unknown')
                return (
                  <tr key={a.id || a.asset_tag} className="border-b last:border-0">
                    <td className="py-1">{a.asset_tag}</td>
                    <td className="py-1">{a.product_name || a.name || '—'}</td>
                    <td className="py-1">{dateStr}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
